/**
 * The column on the left of the page.
 *
 * Expects one entry per section, the two filters with the callbacks that change them, and children.
 *
 * Renders a link per entry with that entry's count beside it, the two filter checkboxes, and the
 * children under the filters.
 *
 * From Tailwind's xl width (1280px) up the column sits beside the sections and is sticky, so the
 * children grow downward inside it and move nothing else on the page. It is at most as tall as the
 * window less its distance from the top (xl:top-4), and scrolls on its own past that, so its foot
 * stays reachable while it is taller than the window. Below xl, TaxProfile.tsx stacks it above the
 * sections, where the links and the filters wrap in rows across the page's width instead of one
 * per line, so the sections keep the whole width of the page. TaxProfile.tsx passes
 * DocumentActivity as the children.
 *
 * The entry of the section in view is drawn with a primary-colored left border, bold text and a
 * lighter background, and carries aria-current. Which section is in view is worked out here and
 * handed to the page through onCurrentChange, so the page can put CURRENT_SECTION_CLASS on it:
 *
 * - One IntersectionObserver watches every entry's section. Its root is the element that scrolls
 *   (the <main> of DashboardWrapper.tsx, not the window), and its rootMargin cuts the bottom
 *   IN_VIEW_BOTTOM_MARGIN off that element, so a section counts as in view while any part of it is
 *   in the top part of the scrolling area. The section in view is the first entry, in the order of
 *   the entries, whose section is there.
 * - When the scrolling element is scrolled to its bottom, the section in view is instead the last
 *   entry whose section shows anywhere in that element, because a short last section never reaches
 *   the top part. This is checked on every scroll event of that element as well as on every report
 *   of the observer, since reaching the bottom need not move any section across the top part.
 * - A click on an entry makes that entry the current one, scrolls to its section with scrollToId,
 *   and stops the observer from changing the current entry while the smooth scroll passes the
 *   sections in between. The observer takes over again at the person's next wheel turn, touch or
 *   scrolling key, so a short last section the observer never counts as in view (the page cannot
 *   scroll it up far enough) stays marked after a click on its entry.
 */

import { useEffect, useRef } from 'react';
import type { MouseEvent, ReactNode } from 'react';
import { scrollToId } from '@/pages/TaxProfile/components/DocumentsTable';
import { BRAND_BORDER_CLASS } from '@/pages/TaxProfile/brand';

/**
 * The padding, rounded corners and the brand's thin green border (brand.ts) TaxProfile.tsx puts on
 * every section, current or not, so the
 * glow of CURRENT_SECTION_CLASS is drawn 12px away from the heading and the table on every side,
 * and a section becoming current changes only its shadow, never its size or the page's layout.
 */
export const SECTION_FRAME_CLASS = `rounded-lg p-3 ${BRAND_BORDER_CLASS}`;

/**
 * The glow TaxProfile.tsx adds to the section whose entry is current, on top of
 * SECTION_FRAME_CLASS: a blue shadow down and to the left and a green one up and to the right, in
 * the theme's primary and secondary colors at 12%.
 */
export const CURRENT_SECTION_CLASS =
  'shadow-[-4px_4px_16px_2px_rgba(79,192,255,0.12),4px_-4px_16px_2px_rgba(0,216,144,0.12)]';

/**
 * How much of the bottom of the scrolling area the observer leaves out, so only a section in the
 * top 40% of it counts as in view.
 */
const IN_VIEW_BOTTOM_MARGIN = '0px 0px -60% 0px';

/** The keys that scroll the page, which hand the current entry back to the observer. */
const SCROLL_KEYS = new Set([
  'ArrowUp',
  'ArrowDown',
  'PageUp',
  'PageDown',
  'Home',
  'End',
  ' ',
]);

/**
 * The nearest ancestor of the element whose content scrolls vertically, or null for the window.
 * On the dashboard that is the <main> of DashboardWrapper.tsx, whose overflow-y-scroll makes it the
 * element that moves, while the window itself does not scroll.
 */
const scrollingAncestor = (element: HTMLElement | null): HTMLElement | null => {
  let parent = element?.parentElement ?? null;
  while (parent) {
    const { overflowY } = window.getComputedStyle(parent);
    if (overflowY === 'auto' || overflowY === 'scroll') return parent;
    parent = parent.parentElement;
  }
  return null;
};

/** How close to the bottom, in pixels, the scrolling element counts as scrolled to the bottom. */
const BOTTOM_TOLERANCE = 2;

/** True when the scrolling element (or the window, for null) is scrolled to its bottom. */
const isAtBottom = (root: HTMLElement | null): boolean => {
  if (root) {
    return (
      root.scrollTop + root.clientHeight >= root.scrollHeight - BOTTOM_TOLERANCE
    );
  }
  const page = window.document.documentElement;
  return (
    window.scrollY + window.innerHeight >= page.scrollHeight - BOTTOM_TOLERANCE
  );
};

/**
 * The last of the anchorIds whose section shows anywhere inside the scrolling element (or the
 * window, for null), or undefined when none does.
 */
const lastVisible = (
  anchorIds: string[],
  root: HTMLElement | null
): string | undefined => {
  const area = root
    ? root.getBoundingClientRect()
    : { top: 0, bottom: window.innerHeight };
  return [...anchorIds].reverse().find((anchorId) => {
    const section = window.document.getElementById(anchorId);
    if (!section) return false;
    const box = section.getBoundingClientRect();
    return box.bottom > area.top && box.top < area.bottom;
  });
};

export interface SectionIndexEntry {
  /** The id of the section this entry scrolls to. */
  anchorId: string;
  label: string;
  /**
   * The values needing review for a profile section, the rows waiting for review for the owners,
   * officers and subsidiaries and states tables, and the number of documents for Documents.
   */
  count: number;
  /** True where `count` counts values needing review, which is what the yellow badge means. */
  countsReview: boolean;
}

export interface SectionIndexProps {
  entries: SectionIndexEntry[];
  needsReviewOnly: boolean;
  onNeedsReviewOnlyChange: (value: boolean) => void;
  hideEmpty: boolean;
  onHideEmptyChange: (value: boolean) => void;
  /** The anchorId of the entry drawn as current; null draws none. */
  currentAnchorId: string | null;
  /** Called with an entry's anchorId when its section comes into view or its entry is clicked. */
  onCurrentChange: (anchorId: string) => void;
  /** Rendered under the filters, inside the sticky column. */
  children?: ReactNode;
}

export default function SectionIndex({
  entries,
  needsReviewOnly,
  onNeedsReviewOnlyChange,
  hideEmpty,
  onHideEmptyChange,
  currentAnchorId,
  onCurrentChange,
  children,
}: SectionIndexProps) {
  const asideRef = useRef<HTMLElement>(null);
  /** True from a click on an entry until the person next scrolls the page themselves. */
  const chosenByClick = useRef(false);
  /** The entries' anchorIds joined with spaces, so the observer is made again only when they change. */
  const anchorIdsKey = entries.map((entry) => entry.anchorId).join(' ');

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const anchorIds = anchorIdsKey.split(' ');
    const root = scrollingAncestor(asideRef.current);
    /** The anchorIds of the sections that are in the top part of the scrolling area right now. */
    const inView = new Set<string>();
    /** Marks the last section showing when at the bottom, else the first one in the top part. */
    const markCurrent = () => {
      if (chosenByClick.current) return;
      const current = isAtBottom(root)
        ? lastVisible(anchorIds, root)
        : anchorIds.find((anchorId) => inView.has(anchorId));
      if (current) onCurrentChange(current);
    };
    const observer = new IntersectionObserver(
      (observed) => {
        observed.forEach((entry) => {
          if (entry.isIntersecting) inView.add(entry.target.id);
          else inView.delete(entry.target.id);
        });
        markCurrent();
      },
      { root, rootMargin: IN_VIEW_BOTTOM_MARGIN }
    );
    anchorIds.forEach((anchorId) => {
      const section = window.document.getElementById(anchorId);
      if (section) observer.observe(section);
    });
    const scrolled: HTMLElement | Window = root ?? window;
    scrolled.addEventListener('scroll', markCurrent, { passive: true });
    return () => {
      observer.disconnect();
      scrolled.removeEventListener('scroll', markCurrent);
    };
  }, [anchorIdsKey, onCurrentChange]);

  useEffect(() => {
    const handBack = () => {
      chosenByClick.current = false;
    };
    const handBackOnScrollKey = (event: KeyboardEvent) => {
      if (SCROLL_KEYS.has(event.key)) handBack();
    };
    window.addEventListener('wheel', handBack, { passive: true });
    window.addEventListener('touchstart', handBack, { passive: true });
    window.addEventListener('keydown', handBackOnScrollKey);
    return () => {
      window.removeEventListener('wheel', handBack);
      window.removeEventListener('touchstart', handBack);
      window.removeEventListener('keydown', handBackOnScrollKey);
    };
  }, []);

  const jumpTo = (event: MouseEvent<HTMLAnchorElement>, anchorId: string) => {
    event.preventDefault();
    chosenByClick.current = true;
    onCurrentChange(anchorId);
    scrollToId(anchorId, 'start');
  };

  /*
   * No top margin: the top of this column sits level with the top of the first section's frame
   * (SECTION_FRAME_CLASS) beside it.
   */
  return (
    <aside
      ref={asideRef}
      className={
        'xl:sticky xl:top-4 xl:max-h-[calc(100vh-1rem)] xl:overflow-y-auto ' +
        'self-start w-full xl:w-64 shrink-0'
      }
      aria-label='Sections'
    >
      <div className='bg-base-100 rounded p-3'>
        <h4 className='text-xs font-bold uppercase opacity-70 mb-2'>
          On this page
        </h4>
        <ul className='menu menu-compact flex-row flex-wrap xl:flex-col'>
          {entries.map((entry) => {
            const isCurrent = entry.anchorId === currentAnchorId;
            return (
              <li key={entry.anchorId}>
                <a
                  href={`#${entry.anchorId}`}
                  aria-current={isCurrent ? 'location' : undefined}
                  onClick={(event) => jumpTo(event, entry.anchorId)}
                  className={`flex justify-between gap-2 py-1 border-l-4 rounded-l-none ${
                    isCurrent
                      ? 'border-primary font-bold bg-base-content/10'
                      : 'border-transparent'
                  }`}
                >
                  <span>{entry.label}</span>
                  <span
                    className={`badge badge-sm ${
                      entry.countsReview && entry.count > 0
                        ? 'badge-warning'
                        : 'badge-ghost'
                    }`}
                  >
                    {entry.count}
                  </span>
                </a>
              </li>
            );
          })}
        </ul>

        <div className='divider my-2' />

        <div className='flex flex-row flex-wrap gap-x-4 xl:flex-col xl:gap-1'>
          <label
            className='label cursor-pointer justify-start gap-2 py-1'
            htmlFor='tax-profile-needs-review-only'
          >
            <input
              id='tax-profile-needs-review-only'
              type='checkbox'
              className='checkbox checkbox-xs'
              checked={needsReviewOnly}
              onChange={(event) =>
                onNeedsReviewOnlyChange(event.target.checked)
              }
            />
            <span className='label-text text-xs'>Needs review only</span>
          </label>
          <label
            className='label cursor-pointer justify-start gap-2 py-1'
            htmlFor='tax-profile-hide-empty'
          >
            <input
              id='tax-profile-hide-empty'
              type='checkbox'
              className='checkbox checkbox-xs'
              checked={hideEmpty}
              onChange={(event) => onHideEmptyChange(event.target.checked)}
            />
            <span className='label-text text-xs'>Hide empty fields</span>
          </label>
        </div>

        {children}
      </div>
    </aside>
  );
}
